import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, PenLine, Users } from "lucide-react";
import { api } from "@/lib/api";
import { PublicVideo } from "@/components/hm/PublicVideo";
import { Split, Arrow, Dawn } from "@/components/hm/Basics";
import { LandingNav } from "@/screens/landing/Top";
import { SiteFooter } from "@/components/hm/SiteFooter";

const PILLARS = [
  { id: "academia", icon: BookOpen, title: "Academia", text: "Donde entiende su interior, su sensibilidad y su proceso emocional." },
  { id: "diario", icon: PenLine, title: "Diario privado", text: "Para expresar lo que siente y ordenar su mundo interno." },
  { id: "comunidad", icon: Users, title: "Comunidad", text: "Donde se siente acompañado por otros jóvenes que viven lo mismo." },
];
const PATHS = { base: "Para quienes necesitan bajar el ruido.", grupal: "Para quienes buscan dirección.", individual: "Para quienes quieren integrar todo." };
const STACK = { display: "grid", gap: "0.9em" };
const WRAP_BTN = { whiteSpace: "normal", maxWidth: "100%" };

const Block = ({ id, eyebrow, title, serif, children, after }) => (
  <section className="sec" id={id} style={{ paddingTop: 0 }} data-testid={`parents-${id}`}>
    <div className="wrap">
      <div className="sec-head">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        {title && <Split className="hm-h1" text={title} serif={serif} />}
        <div className="lead rv" style={STACK}>{children}</div>
      </div>
      {after}
    </div>
  </section>
);

const ParentsVideo = () => <PublicVideo slug="padres-video" testid="parents-video" />;

const ParentsPlans = () => {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  return (
    <div className="pillars" style={{ marginTop: 24 }}>
      {plans.map((p, i) => (
        <article key={p.id} className="pillar rv" style={{ "--d": `${i * 90}ms` }} data-testid={`parents-plan-${p.id}`}>
          <h3><small>0{i + 1}</small>{p.title}</h3>
          <p>{PATHS[p.id]}</p>
          <p className="text-muted" style={{ margin: 0 }}>{p.amount} €/mes</p>
        </article>
      ))}
    </div>
  );
};

export default function ParentsPage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  return (
    <div className="welcome-mod force-dark" style={{ marginBottom: 0 }} data-testid="parents-page">
      <LandingNav />
      <main style={{ position: "relative", paddingTop: 90 }}>
        <Dawn style={{ opacity: 0.22 }} />
        <section className="sec" data-testid="parents-hero">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">Para padres y madres</span>
              <Split as="h1" className="hm-h1" text="Si eres padre o madre y ves a tu hijo o hija apagado," serif="esto es para ti." />
              <p className="lead rv">Entiende qué le está pasando, cómo acompañarlo y cómo Heartmony puede ayudarle a recuperar claridad, energía y dirección interna.</p>
            </div>
            <Link to="/#como" className="btn btn-primary btn-hm wm-btn" style={WRAP_BTN} data-testid="parents-hero-cta">Ver cómo funciona Heartmony para jóvenes <Arrow /></Link>
          </div>
        </section>

        <Block id="empatia">
          <p>Si notas a tu hijo o hija sin motivación, sin energía, más sensible de lo habitual o desconectado de sí mismo, no es culpa tuya.</p>
          <p>El mundo va demasiado rápido… y el sistema educativo actual no está preparado para acompañar la profundidad emocional de esta generación.</p>
          <p>Muchos jóvenes sienten vacío, confusión o falta de sentido. Y no saben cómo explicarlo.</p>
        </Block>

        <Block id="que-pasa" eyebrow="Qué está pasando" title="Lo que le pasa" serif="tiene sentido.">
          <p>Tu hijo o hija no está roto.</p>
          <p>Está viviendo un proceso interno que nadie le ha enseñado a comprender: más sensibilidad, más ruido mental, más preguntas, más necesidad de verdad.</p>
          <p>Este momento puede ser confuso, pero también puede convertirse en un punto de inflexión si se acompaña bien.</p>
        </Block>

        <Block id="quien-soy" eyebrow="Quién soy yo" title="Soy Jorge, ingeniero" serif="y explorador del corazón.">
          <p>Tras una crisis profunda dediqué ocho años a investigar cómo el corazón podía ayudarme a recuperar coherencia, claridad y sentido.</p>
          <p>Lo que descubrí me cambió la vida.</p>
          <p>Y hoy acompaño a jóvenes que sienten ese mismo vacío… a través de Heartmony.</p>
        </Block>

        <section className="sec" id="que-es" style={{ paddingTop: 0 }} data-testid="parents-que-es">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">Qué es Heartmony</span>
              <Split className="hm-h1" text="Heartmony: una escuela" serif="de vida para jóvenes." />
              <div className="lead rv" style={STACK}>
                <p>Heartmony no es terapia.</p>
                <p>Es acompañamiento educativo y emocional basado en las últimas investigaciones científicas sobre el corazón.</p>
                <p>Tu hijo o hija encuentra tres pilares:</p>
              </div>
            </div>
            <div className="pillars">
              {PILLARS.map((x, i) => (
                <article key={x.id} className="pillar rv" style={{ "--d": `${i * 90}ms` }} data-testid={`parents-pillar-${x.id}`}>
                  <span className="card-ico"><x.icon /></span>
                  <h3><small>0{i + 1}</small>{x.title}</h3>
                  <p>{x.text}</p>
                </article>
              ))}
            </div>
            <p className="lead rv" style={{ marginTop: 24 }}>Sin juicio. Sin prisa. Con verdad.</p>
          </div>
        </section>

        <section className="sec" id="video" style={{ paddingTop: 0 }} data-testid="parents-video">
          <div className="wrap">
            <div className="sec-head">
              <span className="eyebrow">Vídeo · 75 segundos</span>
              <Split className="hm-h1" text="Mira este vídeo: en 75 segundos entenderás" serif="exactamente cómo acompaño a tu hijo o hija." />
            </div>
            <div style={{ maxWidth: 880 }}><ParentsVideo /></div>
            <div className="wm-cta rv" style={{ justifyContent: "flex-start" }}>
              <Link to="/#planes" className="btn btn-primary btn-hm wm-btn" style={WRAP_BTN} data-testid="parents-video-plans-cta">Ver los planes de Heartmony <Arrow /></Link>
            </div>
          </div>
        </section>

        <Block id="planes" eyebrow="Planes" title="Tres planes para" serif="acompañar su proceso." after={<ParentsPlans />}>
          <p>Cada joven avanza a su ritmo. Por eso Heartmony tiene tres caminos:</p>
        </Block>

        <section className="wrap" data-testid="parents-final">
          <div className="final">
            <Dawn />
            <span className="eyebrow">Tu primer paso</span>
            <Split className="hm-h1" style={{ marginTop: "1.2rem" }} text="Si tu hijo o hija se siente perdido, no está roto." serif="Solo necesita aprender a escucharse." />
            <Link to="/modulo-bienvenida" className="btn btn-primary mag" data-testid="parents-final-cta">Empezar ahora <Arrow /></Link>
            <p className="text-muted" style={{ marginTop: 16, fontSize: 14 }} data-testid="parents-final-note">Sin tarjeta para empezar · Proceso privado · Acompañamiento real</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
